import { cva } from "class-variance-authority";

/**
 * Full-bleed shell: horizontal page gutters only. **Do not** combine `px-*` with `max-w-*` on the
 * same box — `box-border` counts padding inside `max-width`, which shrinks children by **2×** gutter
 * and makes the white card **32px** narrower than Figma (e.g. 598 vs 630, 550 vs 582).
 */
export const registrationValuePropPageShellStyles = cva([
  "w-full px-4 py-8 md:py-12 lg:py-16",
]);

/**
 * Centered column: **no** horizontal padding — width matches Figma card (582 tablet / 630 desktop).
 * Nested inside `registrationValuePropPageShellStyles`.
 */
export const registrationValuePropRootStyles = cva([
  "w-full max-w-[630px] md:max-w-[582px] lg:max-w-[630px] mx-auto",
  "flex flex-col items-center gap-4 md:gap-6 lg:gap-6",
]);

/**
 * **Mobile (`< md`)**: Figma registration — no white card; content sits on page background (`overflow-visible`, no pad/shadow/ring).
 * **Tablet+ (`md`)**: white card **582×729** / **630×729** (`lg`), **32px** padding (`p-8`). `ring` (not `border`) avoids border-box width theft.
 */
export const registrationValuePropCardStyles = cva([
  "w-full max-w-[630px] md:max-w-[582px] lg:max-w-[630px] min-w-0 box-border",
  "flex flex-col overflow-visible md:overflow-hidden",
  "rounded-none bg-transparent p-0 shadow-none ring-0",
  "md:rounded-2xl md:bg-surface md:p-6 md:shadow-sm md:ring-1 md:ring-inset md:ring-outline/10",
]);

/** Figma: **518×665** (`md` tablet) / **566×665** (`lg`); mobile height is content-driven. Gap between image and copy: **16px** mobile, **24px** tablet+. */
export const registrationValuePropCardInnerStyles = cva([
  "box-border flex w-full min-w-0 max-w-[566px] md:max-w-[518px] lg:max-w-[566px] shrink-0 flex-col gap-6 overflow-x-hidden",
]);

/**
 * Fixed-height crop window for phone — same height for every slide (no layout shift).
 * Figma mask window ~313×300 (actual asset size).
 */
export const registrationValuePropPhoneStageStyles = cva([
  "relative mx-auto w-full min-w-0 max-w-[478px] h-[300px] shrink-0 overflow-hidden flex items-center justify-center",
]);

/** Pre-cropped slide art — fit entire asset in the fixed stage (charts/gauges are wide). */
export const registrationValuePropSlideImgStyles = cva([
  "pointer-events-none z-[1] object-contain object-center",
]);

/**
 * Title + body region: fixed height to prevent layout shift.
 * Gap between headline and body: **16px** (Figma updated spacing).
 */
export const registrationValuePropCopyBlockStyles = cva([
  "relative z-[1] flex w-full min-w-0 max-w-full shrink-0 flex-col justify-start gap-4 md:gap-3 lg:gap-3 overflow-hidden text-left",
  "h-[152px] md:h-[108px] lg:h-[108px]",
]);

/** Reserved headline band — keeps line count visually stable; overflow clips if i18n exceeds clamp. */
export const registrationValuePropHeadlineSlotStyles = cva([
  "min-w-0 shrink-0 overflow-hidden",
  "h-[72px] md:h-[36px] lg:h-[36px]",
]);

/** Body fills remaining copy height; **overflow hidden** (no scrollbar — Figma). */
export const registrationValuePropBodySlotStyles = cva([
  "min-h-0 min-w-0 flex-1 overflow-hidden",
  "h-[80px] md:h-[72px] lg:h-[72px]",
]);

/**
 * Card + carousel: Figma mobile **24px** between sections (`gap-6`); tablet+ **24px** (`gap-6`) to white card frame.
 */
export const registrationValuePropCardNavStackStyles = cva([
  "flex w-full min-w-0 flex-col items-center gap-6",
]);

/** Carousel arrows + dots — outside the card (same max width as card per breakpoint). */
export const registrationValuePropCarouselRowStyles = cva([
  "flex w-full max-w-[630px] md:max-w-[582px] lg:max-w-[630px] items-center justify-center gap-6",
]);

/** Heading: **24px / 32px** mobile/tablet, **28px / 36px** desktop. Headline Medium typography. Line clamp matches fixed headline slot. */
export const registrationValuePropHeadlineStyles = cva([
  "m-0 min-w-0 max-w-full break-words text-[#003049] font-medium text-[24px] md:text-[24px] lg:text-[28px] leading-[32px] md:leading-[32px] lg:leading-[36px] tracking-tight",
  "line-clamp-2 md:line-clamp-2 lg:line-clamp-1",
]);

export const registrationValuePropBodyStyles = cva([
  "m-0 block min-w-0 max-w-full break-words text-on-surface text-[12px] md:text-[12px] lg:text-[14px] leading-[18px] md:leading-[18px] lg:leading-[20px] tracking-[0.25px]",
  "line-clamp-2 md:line-clamp-3 lg:line-clamp-3",
]);

/** Figma mobile CTA stack **12px** gap (`gap-3`); slightly looser from `md` when row layout. */
export const registrationValuePropCtaRowStyles = cva([
  "flex w-full max-w-[630px] md:max-w-[582px] lg:max-w-[630px] flex-col-reverse gap-4 md:gap-4 md:flex-row md:items-center md:justify-between",
]);

/** Login link row below CTA buttons — centered text with responsive max-width matching card constraints. Gap: 16px mobile, 24px tablet+. */
export const registrationValuePropLoginLinkRowStyles = cva([
  "flex w-full max-w-[630px] md:max-w-[582px] lg:max-w-[630px] items-center justify-center gap-4 md:gap-6 text-center",
]);
